// ---- wanderlore-game client: bootstrap ----
// Owns the scene, the day/night cycle, world/player setup, and the game loop.
// Everything else lives in focused modules under ./src.

import * as THREE from "three";
import { buildWorld, freezeStatic, onRoad } from "./src/world.ts";
import type { MapDot, LightSpot } from "./src/world.ts";
import { mapById, loadedMap, activateMap, cinematicById } from "./src/content/load.ts";
import { ACTIVE_MAP_ID } from "./src/map/active.ts";
import type { MapDoc } from "./src/content/schemas.ts";
import { navResolve, clampToShore, onDeckOverWater, wadeClamp, slopeGate, airWallGate, navStandAt, navSurfaceSupport, navSurfacesNear, isWading, type Collider, type Surface } from "./src/physics.ts";
import { V, stepVertical, groundVert } from "./src/sim/vertical.ts";
import { buildPlayer, updatePlayer, setWeapon, setShield, setTool, startAttack } from "./src/player.ts";
import { attackDuration, type AttackSel } from "./src/anim/pose.ts";
import { heightAt, rippleWater, WATER_LEVEL } from "./src/terrain.ts";
import { setAirborne, popLanding, applyAirborne } from "./src/anim/airborne.ts";
import { initSplash, spawnSplash, updateSplashes } from "./src/splashfx.ts";
import { initMinimap, updateMinimap } from "./src/minimap.ts";
import { initImDebug, updateImDebug } from "./src/imdebug.ts";
import { initSettingsUI, onQualityChange, settings } from "./src/settings.ts";
import { initAudio, setAudioMap, resetAudio, updateAudio } from "./src/audio.ts";
import { makeDayNight } from "./src/daynight.ts";
import { SWAY_TIME, updateDecoView, registerCulled, type CulledDeco } from "./src/instanced.ts";
import { initEdgePreview, updateEdgePreview, disposeEdgePreview } from "./src/edge/preview.ts";
import { initEdgeCue } from "./src/edge/cue.ts";
import { connect, resumeHere, sendMove, updateRemotes, updateNetNpcs, sampleRenderT, remoteDots, sendPickup, sendDrop, sendEquip, sendEquipTool, sendWear, sendDropObol, sendBuy, sendSell, sendAcceptGift, sendCinematicDone, resetMirrors, finishTransfer, armHold, type SpawnInfo, type TransferInfo } from "./src/net.ts";
import { $ } from "./src/dom.ts";
import { keys, game, inputLocked } from "./src/state.ts";
import {
  WALK_SPEED, RUN_SPEED, ROAD_BOOST, BOUND, MAX_LIGHTS, LAMP_INTENSITY, LAMP_SHADOW_MAP, CAM_ORBIT_SPEED,
} from "./src/config.ts";
import { makeRenderer } from "./src/render.ts";
import { makeCamera, updateCamera, updateTalkCamera, updateTalkExitCamera, updateJournalCamera, updateJournalExitCamera, updateFishingCamera, updateFishingExitCamera, updateAimCamera, updateAimExitCamera, updateCookCamera, updateCookExitCamera } from "./src/camera.ts";
import { addChat, initChat } from "./src/chat.ts";
import { initInput } from "./src/input.ts";
import { initSignin, showSignin, hideSignin, showResetView, showAuthNote, showLoading, hideLoading, showDisplaced, fadeOut, fadeIn } from "./src/ui.ts";
import { initCharSelect, showCharSelect } from "./src/charselect.ts";
import { initNpcs, updateNpcs, disposeNpcs } from "./src/npc/index.ts";
import { disposeGroup } from "./src/meshkit.ts";
import { registerCutaways, updateCutaway } from "./src/cutaway.ts";
import { initFocus, updateFocus, getFocus, clearFocus, clearPropFocus, clearFireFocus } from "./src/focus.ts";
import { initInteract, updateInteract, setInteractWorld } from "./src/interact.ts";
import { setDoorWorld, disposeDoors, updateDoors } from "./src/doors.ts";
import { initSeats, setSeatWorld } from "./src/seat.ts";
import { initFishing, updateFishing, getCastPoint, resetFishing } from "./src/fishing.ts";
import { initFish, updateFish, resetFish } from "./src/fish.ts";
import { initHunting, updateHunting, getAimTarget, resetAiming } from "./src/hunting.ts";
import { initCooking, updateCooking, eat, startCooking, getCookPoint, resetCooking } from "./src/cooking.ts";
import { initWoodcutting, updateWoodcutting, resetWoodcutting } from "./src/woodcutting.ts";
import { initFirestarting, updateFirestarting, resetFirestarting, tryStartFire } from "./src/firestarting.ts";
import { initBrewing } from "./src/brewing.ts";
import { initCampfires, updateCampfires, resetCampfires, getFires } from "./src/campfire.ts";
import { initCombat, updateCombat, playerPunch } from "./src/combat.ts";
import { initCombatFx, clearCombatFx } from "./src/combatfx.ts";
import { initProjectiles, updateProjectiles, clearProjectiles } from "./src/projectile.ts";
import { initTalk, updateTalk, forceEndTalk } from "./src/talk.ts";
import { initShop } from "./src/shop.ts";
import { initGift } from "./src/gift.ts";
import { initWallet, setWallet } from "./src/wallet.ts";
import { initJournal } from "./src/journal.ts";
import { initQuestHud, updateQuestHud, refreshQuestHud } from "./src/questhud.ts";
import { initCoach, updateCoach, refreshCoach } from "./src/coach.ts";
import { updateVitals, setCharacter } from "./src/hud.ts";
import { initWorldItems, updateWorldItems, pickItemAt, worldItemPos, worldItemType } from "./src/items/world.ts";
import { initWorldCursor, updateWorldCursor } from "./src/cursor.ts";
import { PICKUP_RANGE, inReach } from "./src/shared.ts";
import { initInventory, hasFreeSlot, flashToast, hydrateInventory, equippedWeapon } from "./src/inventory.ts";
import { initGear, hydrateGear } from "./src/gear.ts";
import { itemSpec } from "./src/items/index.ts";
import { isRanged } from "./src/items/define.ts";
import { initWeather, updateWeather, weatherNow, weatherClear, gradeIndoorBlackout } from "./src/weather.ts";
import { initWeatherDev, updateWeatherDev } from "./src/weatherdev.ts";
import { initToonDev } from "./src/toondev.ts";
import { initCameraDev } from "./src/cameradev.ts";
import { initGrantDev } from "./src/grantdev.ts";
import { RIM, TOON_NIGHT } from "./src/toon.ts";
import { initCinematic, setCineHero, setCinematicLauncher, buildCineStage, mountCineRuntime, disposeCineStage, updateCinematic } from "./src/cinematic/index.ts";

// ---- Scene ----
const scene = new THREE.Scene();
initSplash(scene); // water-landing rings (gravity plan)

// ---- Day/night (#2): owns the sun, sky, fog, hemisphere + ambient ----
const dayNight = makeDayNight(scene);

// ---- Weather: grades the atmosphere daynight set up (and owns fog.near/far),
// plus the moon, precipitation, clouds and lightning. Server-driven; see
// src/weather.ts (render half) + src/server/weather.ts (the sim).
initWeather(scene, dayNight);

// ---- World ----
// Built at SPAWN time, not module init (plan 21): the `spawn` message names the
// map this character is on, and initWorld (below) builds THAT doc. Everything
// world-derived is a `let` seeded empty so the pre-spawn frames (behind the
// loading overlay / sign-in form) run safely against no world.
let world: THREE.Group | null = null;
let water: THREE.Object3D | null = null;
let surfaces: Surface[] = [];
// Layer 1 marks the STATIC world — the only geometry allowed into the night
// lamps' BAKED shadow maps. A moving object baked into a static map leaves its
// silhouette frozen on the ground, so dynamic casters must stay out. Three.js
// tests layers against the MAIN camera during shadow passes (not the shadow
// camera's), so a shadow-camera mask can't enforce this; instead
// setNightCasters() flips castShadow OFF on every mesh NOT tagged here while
// the lamps are lit (see updateLamps) — free of visual cost, because the sun
// (the only thing that gives characters a shadow) is down whenever lamps are on.
const LAMP_OCCLUDER_LAYER = 1;

// Warm point lights (#2) at lit structures — ramped up at night. The world can
// emit more light spots than the shader budget (MAX_LIGHTS), so rather than
// keeping a fixed first-N, we maintain MAX_LIGHTS movable lamps and reassign them
// each frame to the spots NEAREST the player. Distant spots are out of range
// (18u) anyway, so the player always sees the lights that matter and structures
// scale freely. L.y is each spot's final world height (scatter lights bake their
// lift in world.ts; structure lights carry their interior height), so it's used
// as-is — re-lifting once shoved the castle torches through the roof.
let lightSpots: LightSpot[] = [];
let lamps: THREE.PointLight[] = [];
// Which spot each lamp currently holds (index into lightSpots), for stable
// assignment — a lamp only moves (and re-bakes) when its spot leaves its
// group's set, not when rank order shuffles within it.
let lampSpot: number[] = [];
let lampDirty = 0; // shadowed lamps left to re-bake (staggered one per frame)

/** Rebuild the lamp pool over the world's static light spots + every live
 *  campfire (plan 25 — a deployed fire has no build-time LightSpot, so the
 *  campfire mirror re-triggers this on every add/remove; ≤MAX_LIGHTS lights,
 *  cheap). No-op mid-swap: the post-swap `fires` replay re-triggers it. */
function refreshLights(): void {
  if (!world) return;
  for (const l of lamps) {
    scene.remove(l);
    l.shadow?.map?.dispose();
    l.dispose();
  }
  lightSpots = [
    ...(world.userData.lights as LightSpot[]),
    ...getFires().map((f) => ({ x: f.x, y: heightAt(f.x, f.z) + 0.8, z: f.z })),
  ];
  buildLamps();
  // buildLamps re-seeds lampSpot to -1 (every lamp re-assigns + re-bakes fresh),
  // so a pending staggered stale-rebake is moot — and MUST be cleared: the pool
  // just shrank/grew, so a leftover countdown would index past the new `lamps`
  // length and throw in updateLamps (e.g. the last campfire burning out at night).
  lampDirty = 0;
}

function buildLamps(): void {
  lamps = Array.from({ length: Math.min(MAX_LIGHTS, lightSpots.length) }, (_, i) => {
    const pl = new THREE.PointLight(0xffb060, 0, 18, 2); // warm, range 18, physical decay
    // Occlusion: without a shadow map a point light shines straight through
    // geometry (a hearth lit the OUTSIDE of its own wall). The nearest
    // `lampShadows` lamps (a quality-preset knob — Low 0 / Med 2 / High 4) get
    // one; anything with castShadow blocks their light (window glass opts out
    // via noShadow in structure/define.ts, so lamplight leaks through windows
    // but not walls). The rest run unshadowed — at 5th+-nearest their 18u range
    // barely reaches anything that could show bleed.
    if (i < settings().lampShadows) {
      pl.castShadow = true;
      pl.shadow.mapSize.set(LAMP_SHADOW_MAP, LAMP_SHADOW_MAP);
      pl.shadow.camera.near = 0.3;
      pl.shadow.camera.far = 18; // match the light's range — tighter depth precision
      pl.shadow.bias = -0.005;
      pl.shadow.normalBias = 0.05;
      // STATIC baked shadows: a point light's cube map is 6 render passes, so
      // auto-updating even a few lamps every frame tanks the frame rate. Bakes
      // contain only the static world (setNightCasters keeps dynamic meshes out),
      // so a map stays valid until the lamp moves to a new spot or the deco
      // live-set is rewritten — both marked via shadow.needsUpdate in updateLamps.
      // No per-frame shadow passes.
      pl.shadow.autoUpdate = false;
    }
    scene.add(pl);
    return pl;
  });
  lampSpot = lamps.map(() => -1);
  lightRank = lightSpots.map((_, i) => i);
}

// Keep dynamic meshes (characters, NPCs, items — anything not on the static
// occluder layer) OUT of the lamps' baked shadow maps by turning their
// castShadow off while the lamps are lit. Runs before every bake (idempotent —
// re-sweeping also catches meshes spawned since the last one) and restores the
// stashed flags at dawn, when the sun needs them casting again.
let nightCastersOff = false;
let lastCasterSweep = 0; // throttles the full-scene re-sweep (see the bake site)
let sweptSceneCount = -1; // scene.children.length at the last sweep — spawn detector
function setNightCasters(off: boolean) {
  nightCastersOff = off;
  lastCasterSweep = performance.now();
  sweptSceneCount = scene.children.length;
  scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || m.layers.isEnabled(LAMP_OCCLUDER_LAYER)) return;
    if (off) {
      if (m.userData.sunCast === undefined) m.userData.sunCast = m.castShadow;
      m.castShadow = false;
    } else if (m.userData.sunCast !== undefined) {
      m.castShadow = m.userData.sunCast as boolean;
      delete m.userData.sunCast;
    }
  });
}
// Scratch buffer reused each frame to rank spots by distance (no per-frame
// alloc). Sized by buildLamps once the world exists.
let lightRank: number[] = [];
function updateLamps(night: number, px: number, pz: number) {
  const intensity = night * LAMP_INTENSITY;
  // Daytime: hide the lamps entirely so their 6-face shadow cube maps aren't
  // rendered for lights contributing nothing (intensity 0 still pays for shadows),
  // and hand the dynamic casters back to the sun.
  const on = intensity > 0.01;
  for (const l of lamps) l.visible = on;
  if (!on) {
    if (nightCastersOff) setNightCasters(false);
    return;
  }
  // Partial selection: pull the nearest `lamps.length` spots to the front.
  for (let i = 0; i < lamps.length; i++) {
    let best = i;
    for (let j = i + 1; j < lightRank.length; j++) {
      const a = lightSpots[lightRank[j]]!, b = lightSpots[lightRank[best]]!;
      const da = (a.x - px) ** 2 + (a.z - pz) ** 2, db = (b.x - px) ** 2 + (b.z - pz) ** 2;
      if (da < db) best = j;
    }
    if (best !== i) { const t = lightRank[i]!; lightRank[i] = lightRank[best]!; lightRank[best] = t; }
  }
  // Identity-stable assignment, in two groups: shadowed lamps [0, lampShadows)
  // hold the nearest spots, plain lamps the rest. A lamp keeps its spot while
  // that spot stays in its group's set, so rank shuffles within a set don't
  // move lights — only a spot genuinely entering a group costs a shadow bake
  // (6 passes, once, and only for the shadowed group).
  const bake: THREE.PointLight[] = [];
  const assignGroup = (lo: number, hi: number) => {
    const want = new Set<number>();
    for (let r = lo; r < hi; r++) want.add(lightRank[r]!);
    const free: number[] = [];
    for (let i = lo; i < hi; i++) {
      if (lampSpot[i]! >= 0 && want.has(lampSpot[i]!)) want.delete(lampSpot[i]!);
      else free.push(i);
    }
    for (const s of want) {
      const i = free.pop()!;
      const L = lightSpots[s]!;
      lampSpot[i] = s;
      lamps[i]!.position.set(L.x, L.y, L.z);
      if (lamps[i]!.castShadow) bake.push(lamps[i]!); // bake once at the new spot
    }
  };
  const S = Math.min(settings().lampShadows, lamps.length);
  assignGroup(0, S);
  assignGroup(S, lamps.length);
  for (const l of lamps) l.intensity = intensity;
  // Staggered re-bake (one lamp per frame) after the deco live-set rewrote —
  // the scatter is in the shadow maps, so its buffer rewrite staled the bakes.
  if (lampDirty > 0) {
    lampDirty--;
    if (lamps[lampDirty]!.castShadow) bake.push(lamps[lampDirty]!);
  }
  if (bake.length) {
    // Sweep dynamic casters out FIRST — the bake renders later this same frame,
    // and a character caught in it would leave a frozen shadow on the ground.
    // Re-sweeping also catches meshes spawned since the last sweep, BUT a
    // staggered rebake burst (lampDirty counts down one lamp per frame) was
    // re-walking the whole scene graph 4 frames in a row for identical state —
    // throttle to one sweep per burst window. Every dynamic entity (rigs,
    // remotes, items, arrows, fires) attaches directly to the scene, so a
    // changed scene.children.length means something spawned/despawned inside
    // the window — re-sweep or the newcomer's castShadow (spawned true) bakes
    // a frozen silhouette that only heals on the next movement burst. (A mesh
    // added under an EXISTING child — an equipped weapon — can still slip one
    // bake; that heals at the next ~12u-of-movement burst as before.)
    if (
      !nightCastersOff ||
      scene.children.length !== sweptSceneCount ||
      performance.now() - lastCasterSweep > 200
    )
      setNightCasters(true);
    for (const l of bake) l.shadow.needsUpdate = true;
  }
}
/** The instanced deco buffers were rewritten — schedule shadowed-lamp re-bakes. */
function markLampShadowsStale() {
  lampDirty = Math.min(settings().lampShadows, lamps.length);
}

// A preset switch changes how many lamps carry baked shadow maps — the flags +
// maps are set at pool build, so rebuild the pool (refreshLights no-ops until
// the world exists; onQualityChange also fires once immediately on subscribe,
// which is before any world build and therefore also a no-op).
let _lampShadowsApplied = settings().lampShadows;
onQualityChange((s) => {
  if (s.lampShadows === _lampShadowsApplied) return;
  _lampShadowsApplied = s.lampShadows;
  refreshLights();
});

// ---- World build (called from onSpawn — the `spawn` message names the map) ----
// Everything that needs the BUILT world lands here: geometry + physics data,
// deco culling, lamps, minimap bake, NPC rigs (only this map's — plan 21), the
// cutaway registry, and the interactable-prop registry. Runs once per spawn AND
// once per in-session map swap (plan 24 — swapWorld tears the old world down
// with disposeWorld, the exact mirror of this function, then re-enters here).
function initWorld(map: MapDoc): void {
  activateMap(map); // terrain layers + flat zones + size/water scalars → THIS doc
  setAudioMap(map); // ambience + audio zones → the mixer (kicks track fetches)
  world = buildWorld(map);
  scene.add(world);
  // Freeze the static world's matrices (see freezeStatic's doc — the cinematic
  // stage runs the same bake); the piggybacked callback tags every mesh as a
  // static lamp occluder so setNightCasters leaves it casting into the bakes.
  freezeStatic(world, (o) => o.layers.enable(LAMP_OCCLUDER_LAYER));
  water = world.getObjectByName("water") ?? null;
  surfaces = world.userData.surfaces as Surface[];

  // View-distance culling (Phase C): the instanced scatter refreshes its live
  // instance set around the player, and the non-instanced decos (cabins/wells)
  // toggle whole-group visibility — both inside updateDecoView each frame.
  registerCulled((world.userData.culledDecos ?? []) as CulledDeco[]);

  refreshLights(); // world lights + any live campfires (plan 25)

  // Minimap (baked terrain + deco dots, live player marker).
  initMinimap(
    $<HTMLCanvasElement>("#minimap"),
    world.userData.dots as MapDot[],
    world.userData.worldSize as number,
  );

  // NPCs — THIS map's rigs + nameplates + colliders only (the scaling-cliff-1
  // win: other maps' rosters are never built client-side).
  initNpcs(scene, world.userData.colliders as Collider[]);

  // Interior cutaway: hide near walls + overhead when inside a building.
  registerCutaways(world.userData.cutaways);

  // Interactable props (chests/trees/flowers/signs) — the listeners were wired
  // at boot (initInteract); the world's props register now.
  setInteractWorld(world);

  // Structure doors (open/close + swinging leaves) — colliders from the same
  // list the player walks against (world.userData.colliders).
  setDoorWorld(world, world.userData.colliders as Collider[]);

  // Furniture seats (click-to-sit) — same lifecycle: listener at boot
  // (initSeats), points swapped per world build.
  setSeatWorld(world);

  // Seamless map edges: neighbor-terrain previews past linked edges, endless
  // filler + fog haze past unlinked ones. Lazy — geometry builds on approach.
  initEdgePreview(scene, map);

  // Shader prewarm, behind the loading/swap fade: flipping the lamps visible at
  // dusk changes the point-light count baked into every program, which used to
  // recompile every toon variant in one hitchy frame at the player's first
  // nightfall. Compile those variants NOW instead (repeat prewarms are cache
  // hits, so a map swap only compiles genuinely new variants).
  for (const l of lamps) l.visible = true;
  prewarm();
  for (const l of lamps) l.visible = false; // updateLamps re-drives visibility next frame
}

// ---- World teardown (map swap, plan 24) — the mirror of initWorld ------------
// Everything initWorld created is removed/disposed here; module registries that
// self-clear on the next build (ROADS, structureKeepOuts, minimap, cutaway,
// interactables, physics nav) need nothing. Shared caches (terrainMat, pathMat,
// the toonMat color cache, UNIT_BOX/SOFT_BOX) deliberately survive — only
// materials tagged `userData.owned` at creation (water, consolidated structure
// classes) are disposed, via meshkit's disposeGroup.
function disposeWorld(): void {
  if (!world) return;
  disposeEdgePreview(); // strips + queued builds die BEFORE the map state swaps
  registerCulled([]); // stop the per-frame deco culler referencing dead buffers
  disposeDoors(); // live leaf Groups before the world Group is disposed
  for (const l of lamps) {
    scene.remove(l);
    l.shadow?.map?.dispose(); // baked point-light cube maps leak hard otherwise
    l.dispose();
  }
  lamps = [];
  lampSpot = [];
  lightRank = [];
  lightSpots = [];
  lampDirty = 0;
  nightCastersOff = false; // the stashed sunCast flags died with the old meshes
  scene.remove(world);
  disposeGroup(world);
  world = null;
  water = null;
  surfaces = [];
}

/** Put the hero at a server-given pose, level (kills any in-flight arc) — the
 *  one placement both a spawn and a transfer arrival use. */
function placePlayerAt(x: number, y: number, z: number, ry: number): void {
  game.feetY = y;
  groundVert(game.vert, y);
  game.player.position.set(x, y, z);
  game.player.rotation.y = ry;
}

// ---- In-session map swap (plan 24) — the portal reaction ---------------------
// net.ts holds every message that arrives after `transfer` (world items are
// enter-once on the wire — dropping one loses it; the reconnect-mismatch path
// arms the same hold inside net.ts's spawn branch); this sequence tears the old
// world down behind a fade, builds the new doc, repositions, then replays the
// held queue (state enters + the props/nets/traps/brews resyncs the server sent
// right behind the transfer). The player rig is NEVER rebuilt — reposition only.
//
// Swaps are SERIALIZED through a promise chain: a reconnect's spawn can name a
// new map while a swap is still mid-teardown (net.ts's onclose reset lets the
// fresh socket's messages through), and two interleaved teardown/rebuilds over
// the shared world/lamps/surfaces state would corrupt both. The queued swap
// runs only after the first settles — usually collapsing into the same-map
// reposition fast path below.
let swapChain: Promise<void> = Promise.resolve();
function swapWorld(t: TransferInfo): Promise<void> {
  game.transferring = true; // input + outbound moves lock NOW (same handler turn)
  const run = swapChain.then(() => doSwapWorld(t));
  swapChain = run.catch(() => {}); // a failed swap must not wedge later ones
  return run;
}

async function doSwapWorld(t: TransferInfo): Promise<void> {
  game.transferring = true; // re-assert for queued swaps (the prior one cleared it)
  try {
    await doSwapWorldInner(t);
  } catch (e) {
    // A throw anywhere in the swap must NOT wedge the session: net.ts holds every
    // inbound message while `transferring` is set (an unbounded heldMsgs array +
    // a client that processes nothing more), so release the hold + unlock input
    // even on failure — exactly what the cinematic-swap siblings do.
    console.error("world swap failed — releasing the transfer hold", e);
    if (!world) {
      // The throw landed after disposeWorld() but before initWorld() finished:
      // there is no terrain/colliders to hand control back to, and nothing
      // re-streams geometry — reload while still behind the fade and let the
      // fresh join rebuild everything from the server's truth.
      console.error("world torn down mid-swap — reloading");
      location.reload();
      return;
    }
    finishTransfer();
    game.transferring = false;
    hideLoading();
    // fadeOut() ran before the throw and fadeIn() is the LAST line of the happy
    // path — without this the player is handed back control under a permanently
    // opaque #fade overlay (hideLoading clears a DIFFERENT element).
    await fadeIn();
  }
}

async function doSwapWorldInner(t: TransferInfo): Promise<void> {
  if (world && t.map === ACTIVE_MAP_ID) {
    // Already on the target map (a queued reconnect swap landed after the real
    // one finished): just reposition and release the held queue — no teardown.
    placePlayerAt(t.x, t.y, t.z, t.ry);
    finishTransfer();
    game.transferring = false;
    hideLoading();
    return;
  }
  // Close every activity holding a reference into the world being torn down.
  // The server already cleaned its half in transferCharacter; these are the
  // local mirrors (dialogue HUD, cast/cook state machines, aim draw, focus).
  forceEndTalk();
  resetFishing();
  resetFish(); // the old map's fish shadows (the new map's re-enter via state)
  resetCooking();
  resetWoodcutting();
  resetFirestarting();
  resetCampfires(); // the old map's fire mirror (the new map's arrive via `fires`)
  resetAiming();
  clearFocus();
  clearPropFocus();
  clearFireFocus();
  resetAudio(); // zone targets → 0; the layers glide out under the fade
  await fadeOut();
  resetMirrors();      // remotes + NPC net mirrors + ground items (the old map's)
  disposeNpcs(scene);  // this map's rigs + projected DOM (bubbles/bars/labels)
  clearProjectiles();  // in-flight arrows lerp old-map coords on the shared scene
  clearCombatFx();     // CC bursts anchored on the old map
  disposeWorld();
  const doc = mapById(t.map);
  if (!doc) {
    // Content skew (a map this bundle doesn't know) — fall back to the v1 reload;
    // the fresh join rebuilds everything from the server's truth.
    console.error(`transfer names unknown map "${t.map}" — reloading`);
    location.reload();
    return;
  }
  initWorld(doc);
  placePlayerAt(t.x, t.y, t.z, t.ry); // arrival pose — the rig is NEVER rebuilt
  // A weather-off map never receives weather messages (server skips it), so the
  // old map's precipitation would keep falling — snap to clear locally.
  if (doc.env?.weather === false) weatherClear();
  finishTransfer(); // replay the held queue onto the new world
  game.transferring = false;
  hideLoading(); // the reconnect path may still have the connect overlay up
  await fadeIn();
}

// ---- Cinematics (plan 26) — the stage-swap flows ------------------------------
// A cinematic plays on an ISOLATED STAGE (a world built from ITS map doc +
// puppet actors, no server sim). One map is hot per process, so the stage and
// the live world must never coexist — both flows below run on the SAME
// swapChain the portal transfers use, so a reconnect/transfer arriving
// mid-cinematic queues behind it instead of interleaving two world builds.

/** First-login intro: the spawn message named a cinematic (net.ts already armed
 *  the message hold off the cue). Plays BEFORE the first initWorld — cheapest
 *  possible stage: there is no live world to tear down yet. Returns false (and
 *  releases the hold) on content skew so a bad id can never brick login. */
function runLoginCinematic(s: SpawnInfo): boolean {
  const doc = s.cinematic ? cinematicById(s.cinematic) : undefined;
  const cineMap = doc ? mapById(doc.map) : undefined;
  if (!doc || !cineMap) {
    if (s.cinematic) {
      console.error(`spawn cinematic "${s.cinematic}" or its map is unknown — skipping the intro`);
      sendCinematicDone(s.cinematic); // stop the server re-cueing a doc this bundle can't play
      finishTransfer(); // net.ts armed the hold off the cue — release it
    }
    return false;
  }
  const run = swapChain.then(async () => {
    try {
      buildCineStage(cineMap, doc);
      const pb = mountCineRuntime(doc);
      hideLoading(); // reveal the stage (the overlay covered connect→spawn)
      await pb.done;
      await fadeOut();
      disposeCineStage();
      const spawnDoc = mapById(s.map) ?? loadedMap;
      initWorld(spawnDoc);
      scene.add(game.player);
      placePlayerAt(s.x, s.y, s.z, s.ry);
      if (spawnDoc.env?.weather === false) weatherClear();
      finishTransfer(); // replay everything held since the spawn
      sendCinematicDone(doc.id);
      await fadeIn();
    } catch (e) {
      // Recover to a normal spawn — an intro must never strand a login.
      console.error("login cinematic failed — recovering to the normal spawn", e);
      disposeCineStage();
      if (!world) initWorld(mapById(s.map) ?? loadedMap);
      if (!game.player.parent) scene.add(game.player);
      placePlayerAt(s.x, s.y, s.z, s.ry);
      finishTransfer();
      sendCinematicDone(doc.id);
      hideLoading();
      await fadeIn();
    }
  });
  swapChain = run.catch(() => {});
  return true;
}

/** Talk/quest-triggered cinematic (the playCinematic action): swap the LIVE
 *  world out for the stage and back, there-and-back behind fades. The player
 *  rig is hidden (never rebuilt); the server just sees the character idle in
 *  place (input is locked the whole time). */
function runTalkCinematic(id: string): void {
  const doc = cinematicById(id);
  const cineMap = doc ? mapById(doc.map) : undefined;
  if (!doc || !cineMap) {
    console.error(`playCinematic "${id}": unknown cinematic or map`);
    return;
  }
  if (game.cinematic || game.transferring || !game.joined) return; // one at a time, never mid-swap
  const back = {
    map: ACTIVE_MAP_ID,
    x: game.player.position.x, y: game.feetY, z: game.player.position.z,
    ry: game.player.rotation.y,
  };
  game.cinematic = true; // lock input THIS turn (the swapWorld transferring pattern)
  const run = swapChain.then(async () => {
    try {
      armHold(); // every message until the world is back replays afterward
      // Close every activity holding a reference into the world being torn down
      // (the doSwapWorld list).
      forceEndTalk();
      resetFishing();
      resetFish();
      resetCooking();
      resetWoodcutting();
      resetFirestarting();
      resetCampfires();
      resetAiming();
      clearFocus();
      clearPropFocus();
      clearFireFocus();
      resetAudio(); // the doSwapWorld sibling — buildCineStage's setAudioMap fades the stage's own audio in from silence
      await fadeOut();
      resetMirrors();
      disposeNpcs(scene);
      clearProjectiles();
      disposeWorld();
      game.player.visible = false;
      buildCineStage(cineMap, doc);
      const pb = mountCineRuntime(doc);
      await fadeIn(); // reveal the stage
      await pb.done;
      await fadeOut();
      disposeCineStage();
      const backDoc = mapById(back.map) ?? loadedMap;
      initWorld(backDoc);
      game.player.visible = true;
      placePlayerAt(back.x, back.y, back.z, back.ry);
      if (backDoc.env?.weather === false) weatherClear();
      finishTransfer();
      await fadeIn();
    } catch (e) {
      console.error("cinematic failed — restoring the world", e);
      disposeCineStage();
      if (!world) initWorld(mapById(back.map) ?? loadedMap);
      game.player.visible = true;
      placePlayerAt(back.x, back.y, back.z, back.ry);
      finishTransfer();
      await fadeIn();
    }
  });
  swapChain = run.catch(() => {});
}

// ---- Interest-management debug HUD (F3): stats panel + minimap grid/rings ----
initImDebug();
initWeatherDev();
initToonDev();
// Dev bag grant panel (F8) — DEV_COMMANDS=1 server only.
initGrantDev();
// ---- Graphics quality settings (O key → options panel) ----
initSettingsUI();
// Ambient audio mixer + its volume sliders (they append to the O panel, so
// initSettingsUI must have built it first). Unlocks on the first gesture.
initAudio();
// "Switch character" lives in the options panel (O): return to the character-
// select screen. A clean reload guarantees a fresh world build for whichever
// hero is chosen next; the session token persists in localStorage, so the boot
// path lands straight back on the select screen. Account management stays on
// that pre-world screen (in-world offers switching only).
{
  const panel = document.getElementById("gfx-panel");
  if (panel) {
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin-top:10px; border-top:1px solid #3a4a3a; padding-top:10px;";
    const b = document.createElement("button");
    b.textContent = "⇄ Switch character";
    b.style.cssText =
      "width:100%; cursor:pointer; padding:6px 0; border-radius:6px;" +
      "font:12px ui-monospace, monospace; border:1px solid #3a4a3a;" +
      "background:#1a241a; color:#cfe3cf;";
    b.onmouseenter = () => { b.style.background = "#2f6d3f"; };
    b.onmouseleave = () => { b.style.background = "#1a241a"; };
    b.onclick = () => { showLoading("Returning to character select…"); location.reload(); };
    wrap.appendChild(b);
    panel.appendChild(wrap);
  }
}
initEdgeCue(); // "→ <map>" pill near a linked map edge (src/edge/cue.ts)

// ---- Local player ----
game.player = buildPlayer();
game.feetY = heightAt(0, 0);
groundVert(game.vert, game.feetY);
game.player.position.set(0, game.feetY, 0);
scene.add(game.player);

// ---- Camera + renderer ----
const camera = makeCamera();
const { render, toggleBloom, prewarm } = makeRenderer($<HTMLCanvasElement>("#game"), scene, camera, dayNight.sun);

// ---- Wiring ----
initChat();
initInput(toggleBloom);

// Items + inventory. The pickup click handler is registered on the canvas BEFORE
// initFocus, so when a click hits an item it pockets it and stopImmediatePropagation()
// keeps focus.ts's (later-registered) listener from also firing — item pickup wins.
const gameCanvas = $<HTMLCanvasElement>("#game");
// Follow-camera dev panel (F7): zoom/pitch sliders + a drag-orbit on the canvas.
// Registered here so its capture-phase pointer handler beats focus/pickup while open.
initCameraDev(gameCanvas);
// Canvas pointer cursor for world-space clickables (items/NPCs/props/seats/…).
// Sole owner of gameCanvas.style.cursor — systems register probes at their init.
initWorldCursor(gameCanvas);
initWorldItems(scene, gameCanvas);
// Equipping attaches the weapon to the local rig and broadcasts it so remotes
// see it in-hand too (null clears both). Wearing is an intent: the server moves
// the item bag → body slot and echoes gear + bag (gear.ts re-dresses the rig).
initInventory(
  sendDrop,
  (type) => {
    setWeapon(game.player, type ? itemSpec(type) : null);
    sendEquip(type); // server validates ownership; the bag itself is server-owned (Step 2)
  },
  (type) => {
    const w = itemSpec(type).wear;
    if (w) sendWear(w.slot, type);
  },
  (type) => {
    // Offhand shield (plan 14): seat it in the off fist + tell the server
    // (it validates ownership + the one-hand-weapon rule and streams it).
    setShield(game.player, type ? itemSpec(type) : null);
    sendEquip(type, true);
  },
  (type) => {
    // Held fishing rod (plan 10, the tool channel): seat it in the main hand
    // (clearing any weapon mesh) + tell the server (it enforces the same
    // weapon/tool exclusivity and streams it as NetPlayer.tool).
    setTool(game.player, type ? itemSpec(type) : null);
    sendEquipTool(type);
  },
  // Eat a cooked dish (plan 18): consume intent — the HP heal is server-owned.
  (type) => eat(type),
  // Cook a raw ingredient (plan 18): starts the timing minigame (cooking.ts).
  (type) => startCooking(type),
  // Start fire from kindling in the bag (firestarting activity).
  () => tryStartFire(),
);
// The paperdoll (worn-gear row in the inventory panel): clicking a worn piece
// takes it off — gated here on bag room so we never send a doomed intent.
initGear((slot) => {
  if (!hasFreeSlot()) { flashToast("Inventory full"); return; }
  sendWear(slot, null);
});
gameCanvas.addEventListener("pointerdown", (e) => {
  // Match the other click paths (focus.ts / interact.ts): no looting while in a
  // conversation/cutscene, knocked out, reading the journal, fishing, or cooking.
  if (!game.joined || e.button !== 0 || inputLocked()) return;
  const id = pickItemAt(camera, e.clientX, e.clientY);
  if (!id) return; // missed every item → let focus handle the click
  e.stopImmediatePropagation();
  // Must be close enough to reach it (server re-checks authoritatively).
  const pos = worldItemPos(id);
  if (pos && !inReach(game.player.position.x, game.vert.y, game.player.position.z, pos.x, pos.y, pos.z, PICKUP_RANGE)) {
    flashToast("Too far away");
    return;
  }
  // Gate on free space here so we never strand an item server-side (a full bag can
  // still top up a matching stack, e.g. arrows). Obol is currency, not cargo — it
  // credits the wallet, so it's never bag-gated.
  const t = worldItemType(id);
  if (t === "obol" || hasFreeSlot(t ?? undefined)) sendPickup(id);
  else flashToast("Inventory full");
});

// Cinematics (plan 26): MUST register FIRST — its keydown handler swallows the
// whole keyboard (Esc = skip) while a cutscene plays, ahead of every other
// module's Esc (keydown listeners fire in registration order). Also wires the
// talk-action launcher (talk.ts requestCinematic → the there-and-back flow).
initCinematic(scene, camera);
setCinematicLauncher(runTalkCinematic);
// Interactable props (chests/trees/flowers/signs): registers the prop picker +
// the F key + the sign plaque. MUST come before initFocus — its keydown handler
// registers first (an open plaque eats Esc before focus.ts sees it), and the
// picker must exist before the first focus click. The world's props register
// later via setInteractWorld (initWorld — the world builds at spawn).
initInteract();
// Fishing (plan 10): the cast/reel click + Esc handlers MUST register after
// initInteract (plaque Esc still wins) and before initFocus — a water click
// with the rod out casts instead of dropping focus, and mid-cast clicks reel.
initFishing(scene, camera, gameCanvas);
initFish(scene); // the fish-shadow mirror (Living Waters) — render-only, no input
// Hunting (plan 11): the RMB draw + trap slot. After initFishing, before
// initFocus — its Esc cancels the draw ahead of focus.ts, and its canvas guard
// swallows clicks while drawn. The fire action is combat.ts's playerPunch
// (injected — combat.ts imports hunting.ts's slot state, so the direct import
// would cycle).
initHunting(scene, gameCanvas, playerPunch);
// Cooking (plan 18): the cook/pull click + Esc handlers. After initHunting,
// before initFocus — a click during the pull window pulls (winning over focus/
// pickup), and its Esc cancels the cook ahead of focus.ts.
initCooking(scene, gameCanvas);
// Tree-cutting: hatchet + chop minigame. After cooking, before focus (F/Esc).
initWoodcutting();
// Firestarting: bag kindling → Start fire → adjacent orange tile pick → spark
// minigame. After woodcutting, before focus (click + Esc precedence).
initFirestarting(scene, camera, gameCanvas);
// Brewing (plan 18 CP3): the passive Ferment/Collect slot. No input lock/Esc of
// its own, so ordering vs initFocus is irrelevant — just wire the slot button.
initBrewing(scene);
// Campfires (plan 25): burning-fire mirror everyone sees (start is bag-only via
// firestarting). onChanged re-ranks night lamps so a lit fire glows.
initCampfires(scene, refreshLights);
// Furniture seats (click-to-sit): AFTER the item/interact/fishing/cook click
// handlers (they stopImmediatePropagation on their hits) and BEFORE initFocus,
// so clicking a bench sits instead of dropping/attacking focus.
initSeats(camera, gameCanvas);
// Click-to-focus a target (combat camera). Raycasts against NPC meshes, then
// interactable props (nearest hit wins — see focus.ts).
initFocus(scene, camera, gameCanvas, $<HTMLElement>("#focus"));
// Combat: HP/stamina, punches, aggro AI + the action bar (drives fightable NPCs).
initCombat(camera);
initCombatFx(scene); // snare/stun/knockback world VFX
// Projectiles: the flying-arrow layer for ranged combat (server-broadcast, cosmetic).
initProjectiles(scene);
// Wallet: the obol balance pill + drop-quantity popover (server-owned currency).
initWallet(sendDropObol);
// Shop / gift: conversation-gated panels (buy-sell and free giveItem hand-offs).
// Init BEFORE initTalk so their Esc handlers win (close the panel, not the talk).
initShop({ onBuy: sendBuy, onSell: sendSell });
initGift({ onTake: sendAcceptGift });
// Talk: conversations with talkable NPCs (the Talk action-bar slot + dialogue HUD).
initTalk();
// Journal: the book HUD (J / 📖) — quests, completed, and an About Me page.
initJournal();
// Quest tracker (top-center objective) + client-only waypoint marker on the
// active step's target NPC. Pure presentation over quest.ts state.
initQuestHud(scene, camera);
// Tutorial coach (bag glow / equip glow / yellow world arrow) from step `coach`.
initCoach(scene, camera);
// ---- Session / spawn --------------------------------------------------------
// The server owns the character. On join (and every reconnect) it sends a
// `spawn` with the SAVED transform + vitals + weapon + inventory; we place the
// player there and hydrate — this is the position/inventory restore.
function onSpawn(s: SpawnInfo) {
  game.hp = s.hp;
  game.maxHp = s.maxHp;
  // Stamina pool after skill-box bonuses (Wind / Second Wind). Older servers
  // omit these — leave the base defaults from state.ts.
  if (typeof s.maxStamina === "number" && Number.isFinite(s.maxStamina) && s.maxStamina > 0) {
    game.maxStamina = s.maxStamina;
    game.stamina = typeof s.stamina === "number" && Number.isFinite(s.stamina)
      ? Math.min(s.maxStamina, Math.max(0, s.stamina))
      : s.maxStamina;
  }
  game.down = false;
  let swapping = false;
  let cine = false;
  if (!game.joined) {
    // The "player" cinematic actor renders as this hero — stash the look before
    // any cinematic flow can mount its runtime.
    setCineHero(s.look, s.gear, s.name);
    // First-login intro (plan 26): the spawn cue defers the real world build —
    // the cinematic stage builds instead, and runLoginCinematic rebuilds the
    // spawn map + reveals the player after the film. Falls through to the
    // normal flow (returning false) on content skew.
    if (!world && s.cinematic) cine = runLoginCinematic(s);
    // First spawn this session: build the map the server says we're on (plan
    // 21 — the world is DEFERRED to here so it can be any map, any size).
    if (!world && !cine) {
      const doc = mapById(s.map);
      if (!doc) console.error(`spawn names unknown map "${s.map}" — building the default`);
      initWorld(doc ?? loadedMap);
    }
    // (Re)build the local rig in the hero's look + gear. Mid-cinematic the rig
    // stays OFF the scene — the login flow adds it after the stage tears down.
    scene.remove(game.player);
    game.player = buildPlayer(s.look, s.gear);
    if (!cine) scene.add(game.player);
    setCharacter(s.name, s.look); // name + look-tinted portrait in the bottom-right card
    game.joined = true;
  } else if (s.map !== ACTIVE_MAP_ID) {
    // Reconnect landed on a DIFFERENT map than the built world (a transfer
    // raced the disconnect and its message was lost) — run the same in-session
    // swap the portal path uses; the spawn carries the full arrival pose.
    // net.ts already armed the message hold for this spawn; the swap owns the
    // placement + the loading overlay (placing HERE would teleport the player
    // to new-map coordinates on the OLD, still-built world for the fade window).
    swapping = true;
    void swapWorld({ map: s.map, x: s.x, y: s.y, z: s.z, ry: s.ry });
  }
  if (!swapping) placePlayerAt(s.x, s.y, s.z, s.ry); // resume at the saved position
  // Restore the bag + held channels (weapon/offhand/tool leave the bag while
  // equipped — silent attach to the rig). Character-scoped, not world-scoped —
  // safe even mid-swap: the rig survives a swap, only the world rebuilds.
  hydrateInventory(s.inventory, { weapon: s.weapon, offhand: s.offhand, tool: s.tool });
  setWallet(s.obol); // restore the obol balance (server-owned)
  setWeapon(game.player, s.weapon ? itemSpec(s.weapon) : null);
  setShield(game.player, s.offhand ? itemSpec(s.offhand) : null);
  setTool(game.player, s.tool ? itemSpec(s.tool) : null);
  // Adopt the saved worn gear (paperdoll HUD + a re-dress — covers reconnects,
  // where the rig above wasn't rebuilt).
  hydrateGear(s.gear, s.look);
  // Player is placed — the follow camera snaps to it this frame, so fade the
  // loading overlay to reveal the world already at the saved spot. (Mid-swap,
  // doSwapWorld hides it instead; mid-cinematic, runLoginCinematic hides it
  // once the stage is built.)
  if (!swapping && !cine) hideLoading();
  // Quest progress may already be mirrored (or land on the next frame) — refresh
  // the tracker so it appears as soon as we're in-world.
  refreshQuestHud();
  refreshCoach();
  applyAnimBridge();
}

// ---- Studio → game animation bridge (dev only) ------------------------------
// The studio's "Test in game" button opens the real game with a weapon + clip in
// the query string, so an animator lands in-world already armed with the pose
// under test. Dev-gated to localhost and applied ONCE per page load (after the
// first spawn, so it survives the auth/join flow). Purely client-visual — the
// server re-asserts the saved weapon on the next real equip; damage won't
// register without the item in the bag, which is fine for visual tuning. The
// user drives the actual swings with the normal in-game controls.
let animBridgeApplied = false;
function applyAnimBridge() {
  if (animBridgeApplied) return;
  if (location.hostname !== "localhost" && location.hostname !== "127.0.0.1") return;
  const q = new URLSearchParams(location.search);
  if (!q.has("weapon") && !q.has("stance") && !q.has("atk")) return;
  animBridgeApplied = true;

  const weapon = q.get("weapon");
  if (weapon) { const spec = itemSpec(weapon); if (spec) setWeapon(game.player, spec); }
  const stance = q.get("stance");
  if (stance === "ready" || stance === "carry") game.player.userData.combatReady = stance === "ready";
  const atk = q.get("atk");
  if (atk) {
    const sel = atk as AttackSel;
    startAttack(game.player, sel);
    if (q.get("loop") === "1") {
      const period = (attackDuration(game.player, sel) + 0.8) * 1000;
      setInterval(() => { if (game.joined && !game.down) startAttack(game.player, sel); }, period);
    }
  }
}

/** Last hero entered on this browser — auto-reenter after a refresh so the
 *  select screen doesn't tax every iteration. Stale ids are cleared on
 *  needCharacter. Switch-character still opens select intentionally. */
const LAST_CHAR_KEY = "lastCharacterId";

/** Enter the world as the chosen character. The token is already held (stored by
 *  afterAuth); the character-select screen picked which hero to join as (or
 *  last-character auto-enter did). */
function startSession(characterId: string) {
  const token = localStorage.getItem("token");
  if (!token) { showSignin("your session expired — log in again"); return; }
  localStorage.setItem(LAST_CHAR_KEY, characterId);
  showLoading("Entering the world…"); // charselect.enter() already hid the select screen
  connect(scene, { token, characterId }, addChat, {
    onSpawn,
    // Portal / /map transfer: swap worlds in-session behind a fade (plan 24).
    onTransfer: (t) => { void swapWorld(t); },
    // The chosen character wasn't ours / no longer exists → back to select.
    // Also clear the remembered id so the next boot doesn't loop on a ghost.
    onNeedCharacter: () => {
      localStorage.removeItem(LAST_CHAR_KEY);
      hideLoading();
      showCharSelect(token);
    },
    onAuthFail: () => {
      localStorage.removeItem("token");
      hideLoading();
      showSignin("your session expired — log in again");
    },
    // Evicted because this character was opened elsewhere: don't thrash the two
    // sessions — show the notice and let "Play here" take the session back.
    onDisplaced: () => {
      hideLoading();
      showDisplaced(() => { showLoading("Resuming here…"); resumeHere(); });
    },
  });
}

/** After ANY successful auth (login/register/reset/Google/saved token): persist
 *  the token, then either reenter the last hero (seamless refresh) or open
 *  character select when there's no remembered id. */
function afterAuth(token: string) {
  localStorage.setItem("token", token);
  hideSignin();
  const last = localStorage.getItem(LAST_CHAR_KEY);
  if (last) {
    // Skip select — startSession shows the loading overlay. A stale id falls
    // through needCharacter → select (and clears the key).
    startSession(last);
    return;
  }
  hideLoading();
  showCharSelect(token);
}

initSignin({ onAuthed: afterAuth });

initCharSelect({
  onEnter: (characterId) => startSession(characterId),
  onLogout: () => {
    localStorage.removeItem("token");
    // Keep lastCharacterId — same browser re-login auto-enters that hero.
    // A different account's needCharacter path clears a mismatched id.
    showSignin();
  },
});

// Auth deep links from the email / OAuth round-trips: ?token (returned from
// Google), ?reset (the reset email), ?verified (the verify link), ?authError (a
// failed OAuth). Read them, then scrub JUST those keys from the URL so a refresh
// doesn't re-fire them — ?ref=site and anything else are preserved for the WS
// connect's referral logging.
const authParams = new URLSearchParams(location.search);
const oauthToken = authParams.get("token");
const resetToken = authParams.get("reset");
const verified = authParams.get("verified");
const authError = authParams.get("authError");
const AUTH_PARAM_KEYS = ["token", "reset", "verified", "authError"];
if (AUTH_PARAM_KEYS.some((k) => authParams.has(k))) {
  const keep = new URLSearchParams(location.search);
  for (const k of AUTH_PARAM_KEYS) keep.delete(k);
  const qs = keep.toString();
  history.replaceState(null, "", location.pathname + (qs ? "?" + qs : "") + location.hash);
}

// Auto-login: if we already hold a session token, skip sign-in. afterAuth then
// either reenters the last hero or opens character select. Otherwise drop the
// default overlay to reveal the sign-in form.
if (resetToken) {
  // Password-reset link takes precedence — show the "set a new password" view.
  hideLoading();
  showResetView(resetToken);
} else if (oauthToken) {
  // Returned from Google with a fresh session → character select.
  afterAuth(oauthToken);
} else {
  const savedToken = localStorage.getItem("token");
  if (savedToken) {
    afterAuth(savedToken);
  } else {
    hideLoading();
    // Only surface verify/OAuth notices when the player is actually on the
    // sign-in screen (not mid auto-login into the world).
    if (verified === "1") showAuthNote("Email verified — thanks!", "ok");
    else if (verified === "0") showAuthNote("That verification link was invalid or expired.", "err");
    else if (authError === "google") showAuthNote("Google sign-in didn't complete — try again.", "err");
  }
}

// ---- Loop ----
let last = performance.now();
let swayClock = 0;  // accumulated sway time — rate scales with weather wind
// Seconds spent continuously walking on a road; past 1s it earns the road bonus
// (1.25× speed + stamina regen). Resets the moment we step off or stop.
let roadTime = 0;
// Space edge-tracking for the jump (the solver wants the press EDGE — holding
// the key still yields exactly one jump; re-arm is the release itself).
let spaceHeld = false;

// Shortest signed angle to rotate by (wrap a delta into [-π, π]) so the combat
// camera eases the short way around instead of unwinding a full turn.
function shortAngle(a: number): number {
  return THREE.MathUtils.euclideanModulo(a + Math.PI, Math.PI * 2) - Math.PI;
}

function frame(now: number) {
  const dt = Math.min((now - last) / 1000, 0.05);
  last = now;

  // ---- Cinematic mode (plan 26): the runtime owns the frame outright --------
  // Puppets + shot camera + letterbox drive in updateCinematic; the world-
  // coupled systems (movement, combat, focus, activities, net reconcile onto
  // rigs) are all suspended — the live world isn't even built. Ambiance stays:
  // dayNight → weather (ordering load-bearing), deco sway, lamps, render.
  if (game.cinematic) {
    const ct = now / 1000;
    swayClock += dt * (1 + weatherNow().wind * 1.6);
    SWAY_TIME.value = swayClock;
    updateCinematic(dt, ct);
    const night = dayNight.update(game.tod, camera.position);
    updateWeather(dt, ct, camera.position, camera.position, night);
    RIM.night.value = 1 - night * TOON_NIGHT.damp;
    updateLamps(night, camera.position.x, camera.position.z);
    updateAudio(dt); // ambiance stays — and drives the fade resetAudio started
    render();
    requestAnimationFrame(frame);
    return;
  }

  const player = game.player;

  // Focused target (a clicked NPC) drives a "combat" camera + target-relative
  // controls. Grab its live position now; it may be roaming.
  const focus = game.joined ? getFocus() : null;
  let fdx = 0, fdz = 0, fdist = 0;
  if (focus) {
    fdx = focus.group.position.x - player.position.x;
    fdz = focus.group.position.z - player.position.z;
    fdist = Math.hypot(fdx, fdz);
  }
  const locked = focus !== null && fdist > 0.01;

  // Talk: resolve a pending conversation request + keep the dialogue HUD in sync.
  // Runs before movement/jump so an activated conversation gates input THIS frame
  // (it sets game.talking); the cutscene camera is driven below.
  updateTalk(dt);

  // Camera: free-orbit with Q/E normally; when focused, ease the yaw so the
  // camera sits behind the player along the player→target line (both framed).
  if (locked) {
    const want = Math.atan2(-fdx, -fdz);
    game.camYaw += shortAngle(want - game.camYaw) * Math.min(1, dt * 6);
  } else if (!game.talking) {
    // While talking, Q/E is the two-shot orbit (handled in the camera block
    // below); don't ALSO spin the follow yaw — they'd double-apply in the frame
    // the player stands flush against the NPC (fdist ≤ 0.01 ⇒ locked is false).
    if (keys.has("q")) game.camYaw += dt * CAM_ORBIT_SPEED;
    if (keys.has("e")) game.camYaw -= dt * CAM_ORBIT_SPEED;
  }

  // Movement input (WASD) — only once signed in, and not while knocked out /
  // snared / stunned / mid server knockback (server owns XZ then) / power-shot charge.
  let mx = 0, mz = 0;
  const rooted = game.snareT > 0 || game.stunT > 0 || game.kbLocal || game.bowCharging;
  if (game.joined && !inputLocked() && !rooted) {
    if (keys.has("w") || keys.has("arrowup")) mz -= 1;
    if (keys.has("s") || keys.has("arrowdown")) mz += 1;
    if (keys.has("a") || keys.has("arrowleft")) mx -= 1;
    if (keys.has("d") || keys.has("arrowright")) mx += 1;
  }

  const moving = mx !== 0 || mz !== 0;
  // Moving (or jumping) breaks a held body pose. Predict the stand locally for
  // instant feel — the server breaks it authoritatively on the same input (the
  // move handler) and the snapshot echo confirms through net.ts.
  if (game.pose && (moving || keys.has(" ")) && !rooted) {
    game.pose = null;
    player.userData.bodyPose = null;
  }
  // Road bonus: after walking on a road for >1s, move + regen 1.25× faster (an
  // incentive to use the roads). combat.ts reads game.roadBoost for the regen side.
  roadTime = moving && onRoad(player.position.x, player.position.z) ? roadTime + dt : 0;
  const roadBoost = roadTime > 1;
  game.roadBoost = roadBoost;
  // Hold Shift to run — faster movement + the run animation. Running needs stamina
  // (it drains 1/sec in combat.ts); out of stamina, you drop back to a walk. The
  // threshold has hysteresis (must recover to 0.5 to re-sprint, but keep going down
  // to 0) so it doesn't flicker walk/run a frame at a time when fully depleted.
  // Bow combat: no sprint while engaged with a fightable (hold and shoot — walk
  // only). Out of combat a bow still sprints; melee combat can still run.
  // game.fighting is from last frame's updateCombat (set later this frame) —
  // one-frame lag is fine.
  const bow = equippedWeapon();
  const bowCombat = game.fighting && !!bow && isRanged(bow);
  const staFloor = game.running ? 0 : 0.5;
  const running = !bowCombat && moving && keys.has("shift") && game.stamina > staFloor;
  game.running = running;
  // Slow CC multiplies ground speed (server budget mirrors via moveSpeedMul).
  const slowMul = game.slowT > 0 && game.slowMult > 0 && game.slowMult < 1 ? game.slowMult : 1;
  const speed = (running ? RUN_SPEED : WALK_SPEED) * (roadBoost ? ROAD_BOOST : 1) * slowMul;
  if (moving) {
    const len = Math.hypot(mx, mz);
    mx /= len; mz /= len;
    let wx: number, wz: number;
    if (locked) {
      // Target-relative: W toward the target, S away, A/D strafe around it.
      // forward = toward target; right = (-fz, fx) (flipped to match feel).
      const fx = fdx / fdist, fz = fdz / fdist;
      wx = fx * -mz + -fz * mx;
      wz = fz * -mz + fx * mx;
    } else {
      // rotate input by camera yaw so "up" is always away from camera. Must use
      // the SAME handedness as updateCamera's applyAxisAngle(UP, camYaw): the
      // camera offset rotates to (sin, cos)·dist, so forward (W) is (-sin,-cos).
      const cos = Math.cos(game.camYaw), sin = Math.sin(game.camYaw);
      wx = mx * cos + mz * sin;
      wz = -mx * sin + mz * cos;
    }
    // Wading (feet in wet terrain after a water landing — gravity plan):
    // movement is heavily slowed, and the wade clamp below only lets it head
    // back toward dry land. Standing on a pier/bridge deck over water is NOT
    // wading (isWading = inWater && !onDeckOverWater) — bare terrain-only
    // inWater would slow you on the fishing dock.
    const airborne = game.vert.air;
    const hereSurfaces = navSurfacesNear(player.position.x, player.position.z);
    const wading = !airborne && isWading(player.position.x, player.position.z, game.vert.y, hereSurfaces);
    const spd = speed * (wading ? V.wadeSlow : airborne ? V.airCtl : 1);
    let cx = player.position.x + wx * spd * dt;
    let cz = player.position.z + wz * spd * dt;
    // Steep terrain is a real wall now: a GROUNDED step up a face steeper than
    // maxSlope loses its uphill component (cliffs must be jumped off or routed
    // around, not walked up) — unless a walkable surface (stairs, a roof ramp)
    // supports the destination. An AIRBORNE arc skips the slope gate but may
    // not steer INTO a cliff column (it would end up inside the terrain).
    if (airborne) {
      const g = airWallGate(player.position.x, player.position.z, cx, cz, game.vert.y);
      cx = g.x; cz = g.z;
    } else if (navSurfaceSupport(cx, cz, game.vert.y) === null) {
      const g = slopeGate(player.position.x, player.position.z, cx, cz, V.maxSlope);
      cx = g.x; cz = g.z;
    }
    // Push back out of any solid obstacle we'd have walked into (sliding along
    // its edges) — floor-aware, so walls only block on the floor we're actually
    // on — and finally clamp to the world. Grid-gated: only nearby colliders.
    const cand = navResolve(cx, cz, game.vert.y);
    // Water gating: an airborne arc flies over the shoreline freely (landing in
    // water is allowed now — the wade rules take over on touchdown); a wading
    // mover only moves shoreward; a dry grounded mover stops at the bank as
    // always, UNLESS a deck (pier/bridge above the waterline) supports the step.
    const dry = airborne
      ? cand
      : wading
        ? wadeClamp(player.position.x, player.position.z, cand.x, cand.z)
        : onDeckOverWater(cand.x, cand.z, game.vert.y, navSurfacesNear(cand.x, cand.z))
          ? cand
          : clampToShore(player.position.x, player.position.z, cand.x, cand.z);
    player.position.x = THREE.MathUtils.clamp(dry.x, -BOUND, BOUND);
    player.position.z = THREE.MathUtils.clamp(dry.z, -BOUND, BOUND);
    if (!locked) player.rotation.y = Math.atan2(wx, wz);
  }
  // While focused, always face the target — even standing still or strafing.
  if (locked) player.rotation.y = Math.atan2(fdx, fdz);

  const t = now / 1000;
  // Drive instanced wind sway (#3) off an accumulated clock instead of raw t:
  // weather's wind speeds the sway up (storms whip the trees), and because
  // amplitude is baked per-spec, the CLOCK RATE is the free knob.
  swayClock += dt * (1 + weatherNow().wind * 1.6);
  SWAY_TIME.value = swayClock;

  // ---- Vertical motion: THE gravity solver (src/sim/vertical.ts) ----
  // One call owns height: grounded tracking (stairs step up, small drops keep
  // the classic glide), real drops and jumps go ballistic, and a descending arc
  // lands on the highest standable surface under it — terrain, a floor, or (as
  // standable geometry grows) a table/rock/roof. Space is fed as a press EDGE;
  // buffering + coyote time live inside the solver. Skipped while knocked out
  // (the KO topple in updateCombat owns position.y) and mid-transfer (the swap
  // repositions via placePlayerAt).
  const spaceNow = keys.has(" ");
  // Snare/stun/kb: no voluntary jump (knockback loft is server-driven).
  // Long-shot charge also blocks jump (and jump cancels the charge in combat.ts).
  const jumpPressed = spaceNow && !spaceHeld && game.joined && !inputLocked() && !rooted;
  spaceHeld = spaceNow;
  // While server owns knockback residual, skip client vertical integration —
  // corrections stamp feetY/vert.y. Snare/stun without kb still use the solver
  // (no jump pressed) so natural falls work.
  if (!game.down && !game.transferring && !game.kbLocal) {
    const ev = stepVertical(game.vert, player.position.x, player.position.z, jumpPressed, dt, navStandAt);
    game.feetY = game.vert.y;
    if (ev.landed) {
      // Touchdown presentation: a splash on water, a crouch dip on land
      // (deeper past the fall-damage threshold — the server owns the damage).
      // Splash only when feet actually land in the water body — not on a dry
      // deck over wet terrain (fishing pier, bridge).
      if (isWading(player.position.x, player.position.z, game.vert.y, navSurfacesNear(player.position.x, player.position.z))) {
        spawnSplash(player.position.x, WATER_LEVEL, player.position.z);
      } else {
        popLanding(player, ev.landed.fallDist > V.safeFall);
      }
    }
  } else if (game.kbLocal) {
    game.feetY = game.vert.y; // follow corrections only
  }
  setAirborne(player, game.vert.air, game.vert.vy);

  // Drive the player's walk cycle (killed mid-air — feet tuck, no run-in-place);
  // it returns the vertical bob to add to ground.
  const bobY = updatePlayer(player, dt, moving && !game.vert.air, t, running && !game.vert.air);
  player.position.y = game.vert.y + bobY;
  // Body pose root (seated/prone/…): additive drop over the resolved feet
  // height + pitch/roll, zeroed by the blend when no pose is live (so these
  // writes are inert while standing). The KO topple runs later in the frame
  // (updateCombat) and overwrites rotation.z/position.y, so it still wins.
  const poseRoot = player.userData.poseRoot as { dy: number; pitch: number; roll: number } | undefined;
  if (poseRoot) {
    player.position.y += poseRoot.dy;
    player.rotation.x = poseRoot.pitch;
    player.rotation.z = poseRoot.roll;
  }
  // Airborne lean + landing dip (gravity plan) — after the gait/pose writes so
  // the flight presentation wins; the KO topple (updateCombat) still runs later.
  applyAirborne(player, dt);
  updateSplashes(dt);
  // Water ripple at ~20Hz, not every frame — it re-lifts + renormalizes the
  // whole 55k-vert plane on the CPU, and the animation reads the same at 20Hz.
  if (water) rippleWater(t); // one uniform write — the wave runs on the GPU now

  // Refresh which decos are live around the player (early-outs until we've
  // moved far enough for the near set to change). A rewrite stales the lamps'
  // baked shadow maps (the scatter casts into them) — schedule re-bakes.
  if (updateDecoView(player.position.x, player.position.z)) markLampShadowsStale();

  // Edge previews/haze/cue: neighbor strips build ≤2 tiles/frame on approach.
  updateEdgePreview(player.position.x, player.position.z);

  // ---- Multiplayer: relay our transform + advance/animate remote players ----
  // Send the REAL feet height (vert.y — rises with a jump/fall arc; no walk bob,
  // remotes add their own) so others place us on the right floor/stair and see
  // us leave the ground. The server validates it against its ballistic envelope.
  // Mid-transfer, hold outbound moves: they'd carry OLD-map coordinates and the
  // server would clamp the fresh arrival back toward them (plan 24).
  if (game.joined && !game.transferring) sendMove(player.position.x, game.vert.y, player.position.z, player.rotation.y, moving, running);
  updateRemotes(camera, dt, t);

  // Time of day is server-owned now (shared across clients) — net.ts adopts it
  // from each snapshot. Holding T just asks the server to fast-forward (input.ts).
  const night = dayNight.update(game.tod, player.position);
  // Weather MUST grade immediately after dayNight.update (ordering is load-
  // bearing): daynight writes absolute light/sky values each frame, weather
  // multiplies/lerps on top — nothing may run between them that reads the sky.
  updateWeather(dt, t, player.position, camera.position, night);
  // Rim light damps toward night so silhouette edges don't glow in the dark
  // (plan 23; the damp factor is an F6-panel tunable).
  RIM.night.value = 1 - night * TOON_NIGHT.damp;
  updateLamps(night, player.position.x, player.position.z);

  // Minimap: redraw the player marker + every remote player over the baked map.
  updateMinimap(player.position.x, player.position.z, player.rotation.y, remoteDots());
  updateImDebug(player.position.x, player.position.z);
  updateWeatherDev();

  // Camera follows. During a conversation, swing into the side-on two-shot framing
  // the talked-to NPC (= the focused one) and the player; for a short window after
  // it ends, ease back to the follow pose; otherwise trail the player.
  if (game.journalOpen) {
    updateJournalCamera(camera, player, game.camYaw, dt);
  } else if (game.journalExit > 0) {
    game.journalExit = Math.max(0, game.journalExit - dt);
    updateJournalExitCamera(camera, player, game.camYaw, dt);
  } else if (game.fishing) {
    updateFishingCamera(camera, player.position, getCastPoint(), dt);
  } else if (game.fishingExit > 0) {
    game.fishingExit = Math.max(0, game.fishingExit - dt);
    updateFishingExitCamera(camera, player, game.camYaw, dt);
  } else if (game.aiming && getAimTarget()) {
    // Hunting draw (plan 11): the over-the-shoulder shot down the aim line.
    updateAimCamera(camera, player.position, getAimTarget()!, dt);
  } else if (game.aimingExit > 0) {
    game.aimingExit = Math.max(0, game.aimingExit - dt);
    updateAimExitCamera(camera, player, game.camYaw, dt);
  } else if (game.cooking) {
    // Cooking (plan 18): the first-person over-the-fire shot at the campfire ahead.
    updateCookCamera(camera, player.position, getCookPoint(), dt);
  } else if (game.cookingExit > 0) {
    game.cookingExit = Math.max(0, game.cookingExit - dt);
    updateCookExitCamera(camera, player, game.camYaw, dt);
  } else if (game.talking && focus) {
    // Q/E orbit the two-shot during the cutscene (WASD/jump stay locked).
    let orbit = 0;
    if (keys.has("q")) orbit += dt * CAM_ORBIT_SPEED;
    if (keys.has("e")) orbit -= dt * CAM_ORBIT_SPEED;
    updateTalkCamera(camera, player.position, focus.group.position, dt, orbit);
  } else if (game.talkExit > 0) {
    game.talkExit = Math.max(0, game.talkExit - dt);
    updateTalkExitCamera(camera, player, game.camYaw, dt);
  } else {
    // FOV pulls in over the whole power-shot charge (0→1), not only at ready.
    updateCamera(camera, player, game.camYaw, dt, game.bowChargeFrac);
  }

  // Fade away near walls + anything overhead while we're inside a structure.
  // Also eases indoorWeight for the follow-camera dolly + exterior blackout.
  updateCutaway(player.position.x, player.position.z, game.feetY, camera.position, dt);
  // Indoor void grade AFTER cutaway (needs current indoorWeight) and AFTER
  // weather's outdoor atmosphere write — only darkens/shrinks further.
  gradeIndoorBlackout();

  // Server-authoritative NPCs: interpolate their networked transforms onto the
  // local rigs BEFORE updateNpcs (bubbles/cutaway) and updateCombat (reaction
  // animations) read them.
  updateNetNpcs();

  // NPCs: show/position a speech bubble over any NPC the player is near (position
  // now comes from the server via updateNetNpcs).
  updateNpcs(camera, player, game.joined, dt);

  // Combat: player regen, NPC reaction animations, overhead bars + action bar.
  if (game.joined) updateCombat(dt);

  // Focus marker: position + pulse the ring under the focused target (if any).
  updateFocus(t);

  // Quest waypoint: gold ground ring + overhead diamond on the active objective
  // target (client-local — other players never see it).
  if (game.joined) {
    updateQuestHud(t);
    updateCoach(t);
  }

  // Interactables: drop a prop focus the server hid + auto-close a strayed plaque.
  updateInteract();
  updateDoors(dt);

  // Fishing: bobber/float animation, the minigame drive, prompts, KO bail-out.
  updateFishing(dt, t);

  // Fish shadows (Living Waters): interpolate the mirrored fish on the same
  // delayed sample clock the NPC/remote interp uses.
  updateFish(sampleRenderT(), t);

  // Hunting: keep a drawn bow honest (target/bow/KO) + the aim prompt's hit%.
  updateHunting(dt, t);

  // Cooking: face the pan, flicker the flames (local + remote pans), tick the
  // pan minigame, and a safety timeout so a dropped reply can't strand the lock.
  updateCooking(dt, t);
  updateWoodcutting(dt);
  updateFirestarting(dt);

  // Campfires: flicker every burning fire on the map (plan 25).
  updateCampfires(t);

  // World items: hover FX (bob/spin/scale the item under the cursor).
  // Must run before updateWorldCursor so the item hover probe sees this frame's hit.
  updateWorldItems(camera, dt, t);

  // World-space click targets → browser pointer cursor (src/cursor.ts).
  updateWorldCursor(camera);

  // Projectiles: advance any in-flight arrows (spawned from server broadcasts).
  updateProjectiles(dt);

  // Vitals HUD: reflect current HP + stamina into the bottom-right bars.
  updateVitals();

  // Ambient audio: zone weights from the player position → eased layer gains.
  updateAudio(dt);

  render();
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);
